import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import * as S from "@styled";
import * as AppPath from "../model/AppPath";
import styled from "styled-components";
import theme from "styled-theming";
import {
    createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel,
    getSortedRowModel, useReactTable, SortingState
} from '@tanstack/react-table';
import { debounce } from "lodash";

import { ThemeType } from "shared/styles/theme";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import {
    DropdownColumnVisibilityControl, IndeterminateCheckbox, TableBottomControlItem,
    TableBottomControlsContainer, TableModeSelect, TablePagination,
} from "shared/components/Table";
//import { CustomToggleButton } from "shared/components/CustomToggle";
import { errorCustomIcom, pinIcon } from "shared/icons/svgHelpers";
import Time from "shared/Time";
import colors from "shared/styles/colors";
import { Option } from "shared/model/Option";
import { DropdownButtonWithState } from "shared/components/DropdownButton";
import { Utilities, getInitialColumnVisibility, setVisibleColumnsToStore } from "shared/utilities";
import { setRowsPerPage } from "processList/model/RowsPerPage";

import { AppContext } from "../App";
import { ProcessTemplateAppReducerState, ProcessesUsingTemplateType } from "processTemplates/model/AppState";
import {
    setLoading, getProcessTemplates, getAvailableTags, copyProcessTemplates, pinProcessTemplates,
    setEditModeModalOpen, setChangeLogModalOpen, setUsedInModalOpen, saveProcessTemplatesAsCsv,
    setDeleteTemplateIds, setTaggedTemplateIds, exportProcessTemplates, setSelectedTemplateIds, setProcessTemplates,
    // setSwitchVersionModalOpen, pinProcessTemplates, 
} from "../actions/AppActions";
import { ProcessTemplate } from "processTemplates/model/ProcessTemplate";
import ActionButtons from "./ActionButtons";
import Filters, { ProcessTemplateFilter } from "./Filters";
import { Permissions } from "shared/model/AllPermissions";
import {
    visibleTemplatesListColumnsKey, getPossibleColumns, optionalColumnsWithLabels,
    pageSizeOptions, ProcessTemplateFilterType, emptyFilter,
} from "processTemplates/model/List";
import { ProcessTemplateListQueryParams } from "../model/AppPath";
import { ImportTemplateTasksModal } from "./ImportTemplateTasksModal";

const List = () => {
    const location = useLocation();
    const { state, dispatch } = useContext<ProcessTemplateAppReducerState>(AppContext);

    const searchParams = new URLSearchParams(location.search);
    const [filter, setFilter] = useState(AppPath.getInitialFilter(searchParams));
    const [sorting, setSorting] = useState(AppPath.getInitialSorting(searchParams));
    const [pageIndex, setPageIndex] = useState(AppPath.getInitialPageNumber(searchParams));
    const [pageSize, setPageSize] = useState(AppPath.getInitialPageSize(searchParams));

    const [columnVisibilityControlOpen, setColumnVisibilityControlOpen] = useState(false);
    const [columnVisibility, setColumnsVisibility] = useState(getInitialColumnVisibility(visibleTemplatesListColumnsKey, optionalColumnsWithLabels));
    const [rowActionMenuOpenForId, setRowActionMenuOpenForId] = useState<number>();

    useEffect(() => {
        getProcessTemplates(dispatch);
        getAvailableTags(dispatch);
        setLoading(dispatch, false);
    }, []);

    const debounceGetProcessTemplates = useCallback(
        debounce(() => {
            setLoading(dispatch, true);
            getProcessTemplates(dispatch);
            setLoading(dispatch, false);
        }, 300),
        []
    );

    useEffect(() => {
        debounceGetProcessTemplates();
    }, [location.search])

    const hasHighlightedTemplate = () => searchParams.has(ProcessTemplateListQueryParams.highlightTemplate);

    const isTemplateHighlighted = (guid: string) => searchParams.get(ProcessTemplateListQueryParams.highlightTemplate) === guid;

    useEffect(() => {
        setColumnVisibilityControlOpen(false);
        setRowActionMenuOpenForId(undefined);
    }, [state.templateForCreate, state.editedTemplate, state.importModalOpen, state.selectedTemplateIds, pageIndex, pageSize]);

    useEffect(() => {
        if (rowActionMenuOpenForId) {
            setColumnVisibilityControlOpen(false);
        }
    }, [rowActionMenuOpenForId]);

    useEffect(() => {
        if (columnVisibilityControlOpen) {
            setRowActionMenuOpenForId(undefined);
        }
    }, [columnVisibilityControlOpen]);

    const clearSelectedTemplates = () => {
        setSelectedTemplateIds(dispatch, []);
    };

    const handleFilterChange = (newFilter: ProcessTemplateFilter) => {
        clearSelectedTemplates();

        setFilter(newFilter);
        const { type, values } = newFilter;

        const params = new URLSearchParams();
        params.set(ProcessTemplateListQueryParams.FilterCategory, type);
        params.set(ProcessTemplateListQueryParams.FilterText, values.join(','));
        AppPath.pushSearchParamsToHistory(params);

        handlePageChangeClick({ selected: 0 });
    };

    const handlePageChangeClick = (event: { selected: number }) => {
        clearSelectedTemplates();

        setPageIndex(event.selected);

        const params = new URLSearchParams();
        params.set(ProcessTemplateListQueryParams.Page, String(event.selected + 1));
        AppPath.pushSearchParamsToHistory(params);
    };

    const handlePageSizeChange = (newPageSizeOption: Option<number>) => {
        clearSelectedTemplates();

        const newPageSize = newPageSizeOption.value;
        setPageSize(newPageSize);

        const params = new URLSearchParams();
        params.set(ProcessTemplateListQueryParams.PageSize, String(newPageSize));
        AppPath.pushSearchParamsToHistory(params);

        handlePageChangeClick({ selected: 0 });

        setRowsPerPage(newPageSize); // sets the page size to local storage, also used by process list
    };


    const handleSortClick = (id: string) => {
        const desc = sorting.id !== id ? sorting.desc : !sorting.desc;
        setSorting({ id, desc });

        const params = new URLSearchParams();
        params.set(ProcessTemplateListQueryParams.SortBy, id);
        params.set(ProcessTemplateListQueryParams.Desc, String(desc));
        AppPath.pushSearchParamsToHistory(params);
    }

    const handleClearFilterButtonClick = () => {
        setProcessTemplates(dispatch, undefined);
        handleFilterChange(emptyFilter);
    };

    const handleToggleColumnVisibility = (columnAccessor: string) => {
        setColumnVisibilityControlOpen(false);
        const nextColumnVisibility = { ...columnVisibility, [columnAccessor]: !columnVisibility[columnAccessor] };
        setColumnsVisibility(nextColumnVisibility);
        setVisibleColumnsToStore(visibleTemplatesListColumnsKey, nextColumnVisibility);
    };

    const handleEditModalOpen = (template: ProcessTemplate) => {
        setEditModeModalOpen(dispatch, template);
    };

    const handleChangeLogModalOpen = (templateId: number) => {
        setChangeLogModalOpen(dispatch, templateId);
    };

    const handleCurrentVersionUsedInModalOpen = (processTemplate: ProcessTemplate) => {
        setUsedInModalOpen(dispatch, { processTemplate, mode: ProcessesUsingTemplateType.Current });
    };

    const handleOlderVersionUsedInModalOpen = (processTemplate: ProcessTemplate) => {
        setUsedInModalOpen(dispatch, { processTemplate, mode: ProcessesUsingTemplateType.Older });
    };

    const handleDeviatedModalOpen = (processTemplate: ProcessTemplate) => {
        setUsedInModalOpen(dispatch, { processTemplate, mode: ProcessesUsingTemplateType.Deviated });
    };

    const handleTagClick = (tag: string) => {
        const filterTags = filter.type === ProcessTemplateFilterType.Tags ? [...new Set([...filter.values, tag])] : [tag];
        handleFilterChange({ type: ProcessTemplateFilterType.Tags, values: filterTags });
    };

    const handleRowActionMenuButtonClick = (templateId: number) => {
        setRowActionMenuOpenForId((prev) => prev !== templateId ? templateId : undefined);
    }

    const getActionClickHandler = (clickHandler: (templateId: number) => void) => {
        return (templateId: number) => {
            setRowActionMenuOpenForId(undefined);
            clickHandler(templateId);
        };
    };

    // const handleTogglePublished = getActionClickHandler((templateId: number) => {
    //     console.log('todo', templateId)
    // });

    // const handleSwitchVersionButtonClick = getActionClickHandler((templateId: number) => {
    //     setSwitchVersionModalOpen(dispatch, templateId);
    // });

    const handleCopyButtonClick = getActionClickHandler((templateId: number) => {
        copyProcessTemplates(dispatch, [templateId]);
    });

    const handleDeleteButtonClick = getActionClickHandler((templateId: number) => {
        setDeleteTemplateIds(dispatch, [templateId]);
    });

    const handleExportButtonClick = getActionClickHandler((templateId: number) => {
        exportProcessTemplates(dispatch, [templateId]);
    });

    const handleSaveAsCsvButtonClick = getActionClickHandler((templateId: number) => {
        saveProcessTemplatesAsCsv(dispatch, [templateId]);
    });

    const handleTagButtonClick = getActionClickHandler((templateId: number) => {
        setTaggedTemplateIds(dispatch, [templateId]);
    });

    const handlePinButtonClick = getActionClickHandler((templateId: number) => {
        const templateGuid = state.processTemplates!.find(template => template.id === templateId)!.guid;
        pinProcessTemplates(dispatch, [templateGuid]);
    });

    const handleShowChangeLogButtonClick = getActionClickHandler((templateId: number) => {
        setChangeLogModalOpen(dispatch, templateId);
    });

    const areAllTemplatesSelected = () => state.processTemplates !== undefined && state.processTemplates.length === state.selectedTemplateIds.length;

    const isTemplateSelected = (templateId: number) => {
        return state.selectedTemplateIds.some((s => s === templateId));
    }

    const handleSelectAllTemplates = (templateIds: number[]) => {
        if (areAllTemplatesSelected()) {
            clearSelectedTemplates();
        } else {
            setSelectedTemplateIds(dispatch, templateIds);
        }
    }

    const handleSelectTemplate = (templateId: number) => {
        if (isTemplateSelected(templateId)) {
            setSelectedTemplateIds(dispatch, state.selectedTemplateIds.filter((id) => id !== templateId));
        }
        else {
            setSelectedTemplateIds(dispatch, [...state.selectedTemplateIds, templateId]);
        }
    }

    const columnHelper = createColumnHelper<ProcessTemplate>();
    const columns = [
        columnHelper.display({
            id: 'select',
            header: ({ table }) => (
                <IndeterminateCheckbox
                    {...{
                        id: "select-all-rows-selector",
                        checked: areAllTemplatesSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: () => handleSelectAllTemplates(table.getRowModel().rows.map((r) => r.original.id)),
                    }}
                />
            ),
            cell: ({ row }) => (
                <IndeterminateCheckbox
                    {...{
                        id: `select-row-${row.original.id}-selector`,
                        checked: isTemplateSelected(row.original.id),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: () => handleSelectTemplate(row.original.id),
                    }}
                />
            ),
        }),
        columnHelper.accessor('id', {
            header: optionalColumnsWithLabels['id'],
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('guid', {
            header: optionalColumnsWithLabels['guid'],
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('name', {
            header: 'Name',
            cell: info => (
                <S.FlexContainer centerItems>
                    {info.row.original.isPinned && (
                        <>
                            <PinIcon />
                            &nbsp;
                        </>
                    )}
                    <S.FlexContainer style={{ alignItems: 'baseline' }}>
                        {state.userPermissions[Permissions.ProcessTemplateEdit] ? (
                            <S.ProcessRowButton
                                title="Edit"
                                onClick={() => handleEditModalOpen(info.row.original)}
                            >
                                {info.getValue()}
                            </S.ProcessRowButton>
                        ) : (
                            <S.ProcessRowButton>
                                {info.getValue()}
                            </S.ProcessRowButton>
                        )}
                        &nbsp;
                        <S.ProcessRowVersionButton
                            title="Show changelog"
                            onClick={() => handleChangeLogModalOpen(info.row.original.id)}
                        >
                            {info.row.original.majorVersion}.{info.row.original.minorVersion}.{info.row.original.version}
                        </S.ProcessRowVersionButton>
                    </S.FlexContainer>
                </S.FlexContainer>
            ),
        }),
        columnHelper.accessor('description', {
            header: optionalColumnsWithLabels['description'],
            cell: info => info.getValue(),
        }),
        columnHelper.accessor("targetFramework", {
            header: optionalColumnsWithLabels['targetFramework'],
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('frendsVersion', {
            header: optionalColumnsWithLabels['frendsVersion'],
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('tags', {
            header: optionalColumnsWithLabels['tags'],
            cell: info => {
                const tags = info.getValue();
                if (!tags?.length) {
                    return null;
                }
                return (
                    <S.FlexContainer gap={5}>
                        {tags.map((tag, i) => (
                            <S.ProcessTagButton
                                key={i}
                                id={`tag-${tag}-selector`}
                                onClick={() => handleTagClick(tag)}
                            >
                                <S.ProcessTagHole />
                                {tag}
                            </S.ProcessTagButton>
                        ))}
                    </S.FlexContainer>
                )
            }
        }),
        columnHelper.accessor('processesUsingCurrentVersion', {
            header: optionalColumnsWithLabels['processesUsingCurrentVersion'],
            cell: info => (
                <S.FlexContainer centerItems gap={32}>
                    {info.row.original.processesUsingCurrentVersion !== undefined && info.row.original?.processesUsingCurrentVersion?.length > 0 && (
                        <StyledUsedInButtonContainer>
                            <i className="ci-check" />
                            <button
                                title="See processes using current version"
                                onClick={() => handleCurrentVersionUsedInModalOpen(info.row.original)}
                            >
                                {info.row.original.processesUsingCurrentVersion.length}
                            </button>
                        </StyledUsedInButtonContainer>
                    )}
                    {info.row.original.processesUsingOlderVersion !== undefined && info.row.original.processesUsingOlderVersion?.length > 0 && (
                        <StyledUsedInButtonContainer>
                            <i className="ci-warning" />
                            <button
                                title="See processes using older version"
                                onClick={() => handleOlderVersionUsedInModalOpen(info.row.original)}
                            >
                                {info.row.original.processesUsingOlderVersion.length}
                            </button>
                        </StyledUsedInButtonContainer>
                    )}
                    {info.row.original.processesDeviated !== undefined && info.row.original.processesDeviated?.length > 0 && (
                        <StyledUsedInButtonContainer>
                            <div
                                className="custom-error"
                                dangerouslySetInnerHTML={{ __html: errorCustomIcom() }}
                            />
                            <button
                                title="See deviated processes"
                                onClick={() => handleDeviatedModalOpen(info.row.original)}
                            >
                                {info.row.original.processesDeviated.length}
                            </button>
                        </StyledUsedInButtonContainer>
                    )}
                </S.FlexContainer>
            ),
        }),
        columnHelper.accessor('modified', {
            header: optionalColumnsWithLabels['modified'],
            cell: info => Time.toDetailedLocalTime(info.getValue()),
        }),
        columnHelper.accessor('modifier', {
            header: optionalColumnsWithLabels['modifier'],
            cell: info => info.getValue(),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <div style={{ width: "100%" }}>
                    <DropdownColumnVisibilityControl
                        open={columnVisibilityControlOpen}
                        onOpen={() => setColumnVisibilityControlOpen(true)}
                        onHide={() => setColumnVisibilityControlOpen(false)}
                        possibleColumns={getPossibleColumns()}
                        visibleColumns={Object.keys(columnVisibility)
                            .filter(k => columnVisibility[k])
                            .map(k => k)
                        }
                        maxVisibleColumns={10}
                        handleToggleColumn={handleToggleColumnVisibility}
                    />
                </div>
            ),
            cell: cell => (
                <S.FlexContainer
                    className="process-edit-action-container-selector"
                    style={{ justifyContent: "flex-end", width: "100%", flexWrap: "nowrap" }}
                >
                    {state.userPermissions[Permissions.ProcessTemplateEdit] && (
                        <S.RightSplitButton
                            variant={S.ButtonType.solid}
                            className="process-edit-button-selector"
                            onClick={() => handleEditModalOpen(cell.row.original)}
                        >
                            {FRENDS.Localizations.Edit}
                        </S.RightSplitButton>
                    )}

                    <DropdownButtonWithState
                        open={rowActionMenuOpenForId === cell.row.original.id}
                        onHide={() => setRowActionMenuOpenForId(undefined)}
                        id={`template-row-actions-${cell.row.original.id}`}
                        dropDownButtonId={`template-row-actions-dropbtn-${cell.row.original.id}`}
                        dropDownButtonClassName={"row-actions-menu-button-selector"}
                        buttonComponent={(
                            <S.LeftSplitButton
                                className="process-actions-button-selector dropbtn"
                                title="More actions"
                                variant={S.ButtonType.solid}
                                onClick={() => handleRowActionMenuButtonClick(cell.row.original.id)}
                            >
                                <i className="ci-more_horizontal" />
                            </S.LeftSplitButton>
                        )}
                        dropDownContentPullRight={true}
                    >
                        {/*
                        <CustomToggleButton
                            id={`template-publish-toggle-${cell.row.original.id}`}
                            onClick={() => handleTogglePublished(cell.row.original.id)}
                            checked={cell.row.original.listed}
                        >
                            Published &nbsp;
                        </CustomToggleButton>
                        <hr />
                        */}
                        {state.userPermissions[Permissions.ProcessTemplateEdit] && (
                            <>
                                {/* <button
                                    id={`template-switch-version-button-${cell.row.original.id}`}
                                    onClick={() => handleSwitchVersionButtonClick(cell.row.original.id)}
                                >
                                    Switch version
                                </button>
                                <hr /> */}

                                <button
                                    id={`template-copy-button-${cell.row.original.id}`}
                                    onClick={() => handleCopyButtonClick(cell.row.original.id)}
                                >
                                    Copy
                                </button>
                                <button
                                    id={`template-delete-button-${cell.row.original.id}`}
                                    onClick={() => handleDeleteButtonClick(cell.row.original.id)}
                                >
                                    Delete
                                </button>
                            </>
                        )}
                        {state.userPermissions[Permissions.ProcessTemplateView] && (
                            <>
                                <button
                                    id={`template-export-button-${cell.row.original.id}`}
                                    onClick={() => handleExportButtonClick(cell.row.original.id)}
                                >
                                    Export to file
                                </button>
                                <button
                                    id={`template-savecsv-button-${cell.row.original.id}`}
                                    onClick={() => handleSaveAsCsvButtonClick(cell.row.original.id)}
                                >
                                    Save as .csv
                                </button>
                            </>
                        )}
                        {state.userPermissions[Permissions.ProcessTemplateEdit] && (
                            <button
                                id={`template-tag-button-${cell.row.original.id}`}
                                onClick={() => handleTagButtonClick(cell.row.original.id)}
                            >
                                Tag
                            </button>
                        )}
                        <button
                            id={`template-pin-button-${cell.row.original.id}`}
                            onClick={() => handlePinButtonClick(cell.row.original.id)}
                        >
                            Pin
                        </button>
                        <hr />

                        <button
                            id={`template-show-change-log-button-${cell.row.original.id}`}
                            onClick={() => handleShowChangeLogButtonClick(cell.row.original.id)}
                        >
                            Show change log
                        </button>
                    </DropdownButtonWithState>
                </S.FlexContainer>
            ),
        }),
    ];

    const tableSortingState = React.useMemo<SortingState>(
        () => ([sorting]),
        [sorting]
    );

    const table = useReactTable({
        data: state.processTemplates || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        state: {
            sorting: tableSortingState,
            columnVisibility
        },
    });

    const noProcessesTextShown = !state.loading && state.processTemplates?.length === 0 && filter.values.length === 0;

    const clearFiltersButtonShown = !state.loading && state.processTemplates?.length === 0 && filter.values.length > 0;

    const tableShown = !(noProcessesTextShown || clearFiltersButtonShown) && state.processTemplates && state.processTemplates.length > 0;

    const templatesCount = state.processTemplatesCount || 0;

    const pageCount = Math.ceil(templatesCount / pageSize);

    const paginationShown = pageCount > 1;

    return (
        <>
            <S.FlexContainer
                isFullWidth
                justifySpaceBetween
                alignItemsEnd
            >
                <ActionButtons selectedTemplateIds={state.selectedTemplateIds} />
                <Filters
                    filter={filter}
                    onChange={handleFilterChange}
                />
            </S.FlexContainer>

            {noProcessesTextShown && (
                <NoTemplatesTextContainer text="No Process Templates" />
            )}

            {clearFiltersButtonShown && (
                <NoTemplatesTextContainer
                    text="No Process Templates found with filters"
                    onClearFilterButtonClick={handleClearFilterButtonClick}
                />
            )}

            {state.missingTasks.length > 0 && <ImportTemplateTasksModal />}

            {tableShown && (
                <>
                    <S.ResponsiveTableContainer style={{ marginTop: '27px', minWidth: 'fit-content' }}>
                        <S.StyledProcessListTable gridTemplateColumns={`60px repeat(${table.getVisibleFlatColumns().length - 2}, auto) 160px`} id="process-templates-table">
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th key={header.id}>
                                                {header.isPlaceholder && null}

                                                {!header.isPlaceholder && header.column.getCanSort() && (
                                                    <div
                                                        {...{
                                                            className: 'pointer',
                                                            onClick: () => handleSortClick(header.column.id)
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </div>
                                                )}

                                                {!header.isPlaceholder && !header.column.getCanSort() && (
                                                    <>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>

                            <tbody>
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id} data-highlighted={hasHighlightedTemplate() && isTemplateHighlighted(row.original.guid)}>
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </S.StyledProcessListTable>
                    </S.ResponsiveTableContainer>

                    <TableBottomControlsContainer>
                        <TableBottomControlItem justifyContent="start">
                            {paginationShown && (
                                <TablePagination
                                    forcePage={pageIndex}
                                    pageCount={pageCount}
                                    onPageChange={handlePageChangeClick}
                                />
                            )}
                        </TableBottomControlItem>

                        <TableBottomControlItem>
                            <TableModeSelect
                                id="rows-per-page-selector"
                                firstLabel="Show"
                                secondLabel={`Templates out of ${templatesCount} total`}
                                options={pageSizeOptions}
                                value={Utilities.valueFromId(pageSizeOptions, pageSize)}
                                onChange={handlePageSizeChange}
                                menuPlacement="auto"
                            />
                        </TableBottomControlItem>
                    </TableBottomControlsContainer>
                </>
            )}
        </>
    );
};

export default List;

const PinIcon = () => {
    const [themeType] = useUserTheme();
    return (
        <span
            dangerouslySetInnerHTML={{ __html: pinIcon(themeType === ThemeType.dark ? colors.white : undefined) }}
            style={{ display: 'flex' }}
        />
    );
};

const styledUsedInButtonColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.white
});

const StyledUsedInButtonContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    i {
        font-size: 20px;

        &.ci-check {
            color: ${colors.greenPrimary};
        }

        &.ci-warning {
            color: ${colors.orangePrimary};
        }
    }

    div.custom-error {
        display: flex;
        align-items: center;

        svg {
            width: 20px;
            height: 20px;
            fill: ${colors.warningDark};
        }
    }

    button {
        all: unset;
        color: ${styledUsedInButtonColor};
        text-decoration: underline;
        cursor: pointer;
    }
`;

interface INoTemplatesTextContainerProps {
    text: string;
    onClearFilterButtonClick?: () => void;
}

const NoTemplatesTextContainer: React.FC<INoTemplatesTextContainerProps> = ({
    text,
    onClearFilterButtonClick,
}) => (
    <S.FlexContainer
        id="no-process-templates-text-container"
        asColumn
        style={{ textAlign: 'center' }}
    >
        <h3 style={{ margin: '44px 0 12px 0' }}>
            {text}
        </h3>

        {onClearFilterButtonClick && (
            <S.ClearFilterLink
                id="clear-filters-selector"
                onClick={onClearFilterButtonClick}
            >
                {FRENDS.Localizations.ClearFilters}
            </S.ClearFilterLink>
        )}
    </S.FlexContainer>
);